import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { X, ArrowRight, Sparkles, Phone, MessageCircle, Gift } from 'lucide-react';
import { useQuiz } from '../context/QuizProvider';
import SubscribeForm from './SubscribeForm';

/**
 * ConversionAgent
 * ---------------
 * Silent watcher that surfaces a personalized nudge ONCE per session when a
 * visitor either:
 *   (a) shows exit intent — their cursor leaves through the top of the viewport
 *   (b) sits idle for ~90 seconds without clicking a primary CTA
 *
 * The nudge is tailored to which pages they've spent time on. Excluded routes
 * are pages where a nudge would be intrusive (already inside a conversion
 * flow, the admin panel, or the /find-best-fit ad-landing).
 */
const EXCLUDED_PATHS = ['/admin', '/payment', '/find-best-fit', '/book', '/enroll', '/priority-enrollment'];
const IDLE_MS = 90_000;
const STORAGE_KEY = 'ls_conversion_nudge_shown';

const NUDGE_VARIANTS = {
  programs: {
    hook: 'Not sure which class is right?',
    line: 'Take our 60-second quiz and we\'ll match your child to the right program for their age.',
    primary: { label: 'Take the Quiz', action: 'quiz' },
    secondary: { label: 'Book a call with Coach Abrie', to: '/book' },
  },
  about: {
    hook: 'Want to meet Coach Abrie?',
    line: 'A free 15-minute pre-enrollment call is the easiest way to see if we\'re the right fit.',
    primary: { label: 'Book a Free Call', to: '/book' },
    secondary: { label: 'Take the Quiz Instead', action: 'quiz' },
  },
  faq: {
    hook: 'Still have questions?',
    line: 'Coach Abrie is happy to answer them personally — a free 15-minute call is quicker than more reading.',
    primary: { label: 'Book a Free Call', to: '/book' },
    secondary: { label: 'Take the Quiz', action: 'quiz' },
  },
  default: {
    hook: 'Before you go — one quick step?',
    line: 'Book a free 15-minute pre-enrollment call with Coach Abrie, or get an instant class match in 60 seconds.',
    primary: { label: 'Book a Free Call', to: '/book' },
    secondary: { label: 'Take the Quiz', action: 'quiz' },
  },
};

export default function ConversionAgent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { open: openQuiz } = useQuiz();

  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState(NUDGE_VARIANTS.default);
  const visitedPaths = useRef(new Set());
  const idleTimer = useRef(null);
  const armedRef = useRef(false); // guards double-fires

  const isExcluded = EXCLUDED_PATHS.some((p) => location.pathname.startsWith(p));

  // Track visited pages this session (for tailoring the nudge).
  useEffect(() => {
    visitedPaths.current.add(location.pathname);
  }, [location.pathname]);

  const pickVariant = () => {
    const v = visitedPaths.current;
    if ([...v].some((p) => p.startsWith('/programs'))) return NUDGE_VARIANTS.programs;
    if ([...v].some((p) => p.startsWith('/about'))) return NUDGE_VARIANTS.about;
    if ([...v].some((p) => p.startsWith('/faq'))) return NUDGE_VARIANTS.faq;
    return NUDGE_VARIANTS.default;
  };

  const trigger = (source) => {
    if (armedRef.current) return;
    if (sessionStorage.getItem(STORAGE_KEY) === '1') return;
    armedRef.current = true;
    setVariant(pickVariant());
    setVisible(true);
    sessionStorage.setItem(STORAGE_KEY, '1');
    try { window.dispatchEvent(new CustomEvent('ls:conversion_nudge_shown', { detail: { source } })); } catch {}
  };

  // Reset idle timer on any interaction
  const resetIdle = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => trigger('idle'), IDLE_MS);
  };

  useEffect(() => {
    if (isExcluded) return undefined;

    // Exit-intent: cursor leaving through the top of the viewport
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 5) trigger('exit_intent');
    };
    // Also treat page-hide (mobile background swipe / tab switch) as intent
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') trigger('tab_hidden');
    };

    document.addEventListener('mouseout', onMouseOut);
    document.addEventListener('visibilitychange', onVisibility);
    const activityEvents = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach((ev) => window.addEventListener(ev, resetIdle, { passive: true }));
    resetIdle();

    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      document.removeEventListener('visibilitychange', onVisibility);
      activityEvents.forEach((ev) => window.removeEventListener(ev, resetIdle));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExcluded, location.pathname]);

  const dismiss = () => setVisible(false);
  const handleAction = (a) => {
    dismiss();
    if (a.action === 'quiz') { openQuiz(); return; }
    if (a.to) navigate(a.to);
  };

  if (!visible || isExcluded) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-3 sm:p-6 bg-black/40 backdrop-blur-sm"
      onClick={dismiss}
      data-testid="conversion-nudge"
    >
      <div
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl border border-splash-sand p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={dismiss}
          data-testid="conversion-nudge-close"
          className="absolute top-3 right-3 p-1.5 rounded-full text-splash-green/50 hover:text-splash-green hover:bg-splash-cream btn-transition"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="inline-flex items-center gap-2 rounded-full bg-splash-coral/15 text-splash-coralDark px-3 py-1 text-[11px] font-semibold uppercase tracking-widest">
          <Sparkles className="w-3 h-3" /> Before you go
        </div>
        <h3 className="mt-3 font-display font-bold text-2xl text-splash-green leading-tight">{variant.hook}</h3>
        <p className="mt-2 text-sm text-splash-green/75 leading-relaxed">{variant.line}</p>

        <div className="mt-5 space-y-2">
          <button
            type="button"
            onClick={() => handleAction(variant.primary)}
            data-testid="conversion-nudge-primary"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-5 py-3 font-semibold btn-transition"
          >
            {variant.primary.action === 'quiz' ? <Sparkles className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
            {variant.primary.label} <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleAction(variant.secondary)}
            data-testid="conversion-nudge-secondary"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white border border-splash-sand text-splash-green hover:border-splash-teal hover:bg-splash-cream px-5 py-3 text-sm font-semibold btn-transition"
          >
            <MessageCircle className="w-4 h-4 text-splash-teal" />
            {variant.secondary.label}
          </button>
        </div>

        <div className="mt-5 rounded-2xl bg-splash-cream border border-splash-sand p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-splash-coral/15 text-splash-coralDark grid place-items-center">
              <Gift className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-splash-coral">Or claim your freebie</div>
              <div className="text-sm font-semibold text-splash-green">First group lesson on us</div>
            </div>
          </div>
          <SubscribeForm variant="compact" source="exit_intent" testIdPrefix="conversion-nudge-subscribe" />
        </div>

        <button
          type="button"
          onClick={dismiss}
          data-testid="conversion-nudge-dismiss"
          className="mt-3 w-full text-center text-[11px] text-splash-green/50 hover:text-splash-green/80 btn-transition"
        >
          No thanks, keep browsing
        </button>
      </div>
    </div>
  );
}
